import React, { useEffect, useState } from "react";
import { Card, Table, Badge, Alert, Spinner, Form, Button } from "react-bootstrap";
import { fetchManutentionsLivreurs, validerManutentionLivreur } from "./DashboardService";

/**
 * Manutentions chiffrées par les livreurs depuis la fiche reçue par SMS.
 *
 * La saisie du livreur s'applique directement : elle détermine ce que le client
 * paie. Dès qu'elle est chiffrée, la commande quitte le carton des manutentions
 * à confirmer — elle n'était donc visible qu'en ouvrant les commandes une par
 * une, alors que c'est précisément ce qu'il faut relire.
 *
 * Deux actions, et une seule échéance :
 *
 *   **Valider** est un visa de relecture, pas un verrou. Le montant est déjà
 *   appliqué ; valider ne fait que sortir la ligne du carton. Une file
 *   d'attente qui se vide se lit, un journal qui grossit ne se lit plus.
 *
 *   **Éditer** ouvre la commande, où le montant se corrige — ce qui annule le
 *   visa, puisqu'il portait sur l'ancienne valeur.
 *
 * L'urgence ne vient pas de l'âge de la saisie mais de la clôture : une fois la
 * facture émise, ni l'un ni l'autre ne sert plus, seul un avoir régularise.
 * D'où le compte à rebours, et la couleur du carton qui suit.
 */

const PERIODES = [7, 30, 90];

// Mêmes seuils que le carton des manutentions à confirmer : c'est la même
// échéance, elle doit se lire de la même façon.
const SEUIL_CRITIQUE = 5;
const SEUIL_ATTENTION = 10;

const LIBELLES = {
  distance: {
    "0-5": "0-5 m", "5-15": "5-15 m", "15-30": "15-30 m",
    "30-50": "30-50 m", "50+": "plus de 50 m",
  },
  obstacles: {
    aucun: "sans obstacle", quelques: "quelques obstacles", difficile: "accès difficile",
  },
  etage: {
    non_concerne: "plain-pied", rdc_surleve: "RDC surélevé", "1er": "1er étage", "2e_plus": "2e étage ou plus",
  },
  ascenseur: { oui: "avec ascenseur", non: "sans ascenseur" },
  poids_colis: { normal: "poids normal", lourd: "colis lourd, 2 porteurs" },
};

const resumerReponses = (reponses) => {
  if (!reponses) return null;
  const parts = Object.entries(LIBELLES)
    .map(([champ, table]) => table[reponses[champ]])
    .filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

const ManutentionsLivreurs = () => {
  const [jours, setJours] = useState(30);
  const [voirValidees, setVoirValidees] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lignes visées pendant cette session : elles disparaissent sans recharger.
  const [validees, setValidees] = useState({});
  const [enCours, setEnCours] = useState(null);
  const [erreurs, setErreurs] = useState({});

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      setLoading(true);
      try {
        setError(null);
        const resultat = await fetchManutentionsLivreurs(jours, voirValidees);
        if (!annule) {
          setData(resultat);
          setValidees({});
        }
      } catch (e) {
        console.error("Erreur chargement manutentions livreurs:", e);
        if (!annule) setError("Impossible de charger les saisies des livreurs");
      } finally {
        if (!annule) setLoading(false);
      }
    };

    charger();
    return () => { annule = true; };
  }, [jours, voirValidees]);

  const valider = async (id) => {
    setEnCours(id);
    setErreurs((prev) => ({ ...prev, [id]: null }));

    try {
      await validerManutentionLivreur(id);
      setValidees((prev) => ({ ...prev, [id]: true }));
    } catch (e) {
      setErreurs((prev) => ({
        ...prev,
        [id]: e.response?.data?.error || "Validation impossible",
      }));
    } finally {
      setEnCours(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="warning">{error}</Alert>;
  }

  const commandes = (data?.commandes ?? []).filter((c) => !validees[c.id]);
  const jClo = data?.jours_avant_cloture;

  // Ce qui reste a relire, meme quand la vue affiche l'historique : sinon
  // l'en-tete annoncerait « 4 a relire » en montrant les lignes deja relues.
  const resteARelire = Math.max(0, (data?.reste_a_relire ?? 0) - Object.keys(validees).length);

  // L'urgence suit ce qui reste a relire, pas la vue ouverte : consulter
  // l'historique ne doit pas eteindre l'alerte.
  const variant =
    resteARelire === 0 ? "secondary"
      : jClo <= SEUIL_CRITIQUE ? "danger"
        : jClo <= SEUIL_ATTENTION ? "warning"
          : "secondary";

  const enTete = voirValidees
    ? `Saisies de livreur déjà relues${resteARelire > 0 ? ` — ${resteARelire} restante${resteARelire > 1 ? "s" : ""}` : ""}`
    : resteARelire === 0
      ? "Saisies des livreurs"
      : `${resteARelire} saisie${resteARelire > 1 ? "s" : ""} de livreur à relire`;

  return (
    <Card className={`border-${variant}`}>
      <Card.Header
        className={
          variant === "secondary"
            ? "d-flex justify-content-between align-items-center flex-wrap gap-2"
            : `bg-${variant} text-white d-flex justify-content-between align-items-center flex-wrap gap-2`
        }
      >
        <span className="fw-bold">{enTete}</span>

        <div className="d-flex align-items-center gap-2">
          {/* Le compte à rebours, pour que la relecture ait une date limite
              lisible : passé l'émission de la facture, ni valider ni éditer
              ne changent plus rien. */}
          {resteARelire > 0 && (
            <Badge bg="light" text="dark">
              {jClo === 0
                ? "Dernier jour du mois"
                : `${jClo} jour${jClo > 1 ? "s" : ""} avant la clôture`}
            </Badge>
          )}
          {/* Deux vues disjointes, et non un filtre inclusif : allumer
              l'interrupteur montre exactement les lignes relues, donc une liste
              vide veut dire « personne n'a encore validé » et se lit comme tel. */}
          <Form.Check
            type="switch"
            id="voir-manutentions-validees"
            label="déjà relues"
            className={variant === "secondary" ? "" : "text-white"}
            checked={voirValidees}
            onChange={(e) => setVoirValidees(e.target.checked)}
          />
          <Form.Select
            size="sm"
            style={{ width: "auto" }}
            value={jours}
            onChange={(e) => setJours(Number(e.target.value))}
          >
            {PERIODES.map((p) => (
              <option key={p} value={p}>{p} derniers jours</option>
            ))}
          </Form.Select>
        </div>
      </Card.Header>

      <Card.Body className="pb-2">
        {commandes.length === 0 ? (
          <p className="text-muted mb-2">
            {voirValidees
              ? resteARelire > 0
                ? "Aucune saisie relue pour le moment — les " + resteARelire + " en attente sont dans l'autre vue."
                : "Aucune saisie relue sur la période."
              : "Tout est relu. Si des manutentions restent à confirmer, vérifier que le SMS de 17 h part bien aux livreurs."}
          </p>
        ) : (
          <>
            <p className="text-muted mb-3">
              <strong>{Number(data.total_ht).toFixed(2)} € HT</strong> qui partaient jusqu'ici
              à 0 €. Le montant est <strong>déjà appliqué</strong> : valider veut dire « relu,
              c'est bon ». Si un chiffre est faux, éditer la commande — ce qui annule le visa.
            </p>

            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Client</th>
                  <th>Livreur</th>
                  <th>Saisie le</th>
                  <th>Ce qu'il a constaté</th>
                  <th className="text-end">Montant</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {commandes.map((c) => (
                  <tr key={c.id}>
                    <td className="fw-semibold">#{c.id}</td>
                    <td>
                      <div>{c.client}</div>
                      {c.date_livraison && (
                        <small className="text-muted">livrée le {c.date_livraison}</small>
                      )}
                    </td>
                    <td>{c.livreur}</td>
                    <td><small>{c.saisie_le}</small></td>
                    <td>
                      <small>
                        {resumerReponses(c.reponses) || <span className="text-muted">—</span>}
                      </small>
                      {/* Le barème refuse de tarifer 50 m et plus + accès
                          difficile + sans ascenseur : le livreur a décrit
                          l'accès, il reste un montant à négocier. */}
                      {c.a_confirmer && (
                        <div>
                          <Badge bg="warning" text="dark">Hors barème — montant à négocier</Badge>
                        </div>
                      )}
                      {erreurs[c.id] && (
                        <div><small className="text-danger">{erreurs[c.id]}</small></div>
                      )}
                    </td>
                    <td className="text-end">
                      {Number(c.total_ht).toFixed(2)} € HT
                      {c.validee_le && (
                        <div>
                          <small className="text-success">
                            relu{c.validee_par ? ` par ${c.validee_par}` : ""} le {c.validee_le}
                          </small>
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        {/* /edit/ et non /show/ : le formulaire de manutention
                            vit sur l'écran d'édition, la fiche ne fait
                            qu'afficher. */}
                        <a
                          href={`/commande-fournisseur/edit/${c.id}`}
                          className="btn btn-sm btn-outline-secondary"
                          title="Ouvrir la commande pour corriger le montant"
                        >
                          Éditer
                        </a>
                        {/* Une facture émise fige le montant : ni valider ni
                            éditer n'y change quoi que ce soit, seul un avoir
                            régularise. Le bouton le dit au lieu d'échouer. */}
                        {c.verrouillee ? (
                          <Button size="sm" variant="outline-secondary" disabled title={c.motif_verrou}>
                            Facturée
                          </Button>
                        ) : !c.validee_le && (
                          <Button
                            size="sm"
                            variant="success"
                            disabled={enCours === c.id}
                            onClick={() => valider(c.id)}
                          >
                            {enCours === c.id ? "…" : "Valider"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ManutentionsLivreurs;
